import { useEffect, useRef } from 'react';

/**
 * A map that shows the point a lat/long pair describes, and sets that pair when
 * you click or drag the pin.
 *
 * Leaflet is loaded globally from public/index.html, so this waits for
 * `window.L` the same way the Olive merchants map does rather than importing a
 * package that isn't a dependency.
 *
 * @param {string|number} latitude   current value, as typed
 * @param {string|number} longitude
 * @param {number|null} radiusMeters check-in radius, drawn around the pin.
 *                                   Null or 0 when check-in is off, and then
 *                                   no ring is drawn at all.
 * @param {function} onPick          receives { lat, lng }, already rounded to
 *                                   the 6 decimal places the column stores
 */

const FALLBACK_CENTER = [39.5, -98.35]; // continental US, when nothing is set
const FALLBACK_ZOOM = 3;
const POINT_ZOOM = 12;

// Same teardrop as the Olive merchants map, so pins read alike across the console.
const pinIcon = () =>
  window.L.divIcon({
    className: 'hc-location-pin',
    html:
      '<svg viewBox="0 0 24 34" width="24" height="34" xmlns="http://www.w3.org/2000/svg" style="display:block">' +
      '<path d="M12 0c6.6 0 12 5.4 12 12 0 9-12 22-12 22S0 21 0 12C0 5.4 5.4 0 12 0z" fill="#60a5fa" stroke="#3b82f6" stroke-width="1.5"/>' +
      '<circle cx="12" cy="12" r="4" fill="#fff"/>' +
      '</svg>',
    iconSize: [24, 34],
    iconAnchor: [12, 34],
  });

const round6 = (n) => Math.round(n * 1e6) / 1e6;

const LocationPickerMap = ({ latitude, longitude, radiusMeters, onPick, height = 240 }) => {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  // Held in a ref so the click and drag handlers can be bound once and still
  // call the latest callback.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const lat = Number.parseFloat(latitude);
  const lng = Number.parseFloat(longitude);
  const hasPoint = Number.isFinite(lat) && Number.isFinite(lng);

  useEffect(() => {
    let timer;
    const init = () => {
      if (!window.L || !elRef.current) {
        timer = setTimeout(init, 100);
        return;
      }
      if (mapRef.current) return;

      const map = window.L.map(elRef.current).setView(FALLBACK_CENTER, FALLBACK_ZOOM);
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
      }).addTo(map);
      map.on('click', (event) => {
        onPickRef.current?.({
          lat: round6(event.latlng.lat),
          lng: round6(event.latlng.lng),
        });
      });
      mapRef.current = map;
      // The modal sizes its content after mount, so Leaflet's first measurement
      // is of a container that has not settled yet.
      setTimeout(() => map.invalidateSize(), 0);
    };
    init();

    return () => {
      if (timer) clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, []);

  // Follow whatever the fields currently say.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.L) return;

    if (!hasPoint) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.remove();
        circleRef.current = null;
      }
      return;
    }

    const radius = Number.parseFloat(radiusMeters);
    const hasRadius = Number.isFinite(radius) && radius > 0;

    // The circle goes on first so the pin stays clickable above it.
    if (hasRadius) {
      if (!circleRef.current) {
        circleRef.current = window.L.circle([lat, lng], {
          radius,
          color: '#3b82f6',
          weight: 1,
          fillColor: '#60a5fa',
          fillOpacity: 0.15,
          interactive: false,
        }).addTo(map);
      } else {
        circleRef.current.setLatLng([lat, lng]);
        circleRef.current.setRadius(radius);
      }
    } else if (circleRef.current) {
      circleRef.current.remove();
      circleRef.current = null;
    }

    if (!markerRef.current) {
      markerRef.current = window.L.marker([lat, lng], {
        draggable: true,
        icon: pinIcon(),
      }).addTo(map);
      markerRef.current.on('dragend', () => {
        const point = markerRef.current.getLatLng();
        onPickRef.current?.({ lat: round6(point.lat), lng: round6(point.lng) });
      });
      // Frame the whole check-in area, not just its centre.
      if (circleRef.current) map.fitBounds(circleRef.current.getBounds().pad(0.6));
      else map.setView([lat, lng], POINT_ZOOM);
      return;
    }

    markerRef.current.setLatLng([lat, lng]);
    // Only chase the pin when it leaves the view. Recentring on every keystroke
    // fights the admin panning around to check they typed the right place.
    if (!map.getBounds().contains([lat, lng])) {
      map.setView([lat, lng], Math.max(map.getZoom(), POINT_ZOOM), { animate: false });
    }
  }, [lat, lng, hasPoint, radiusMeters]);

  return (
    <div>
      <div
        ref={elRef}
        style={{
          height: `${height}px`,
          width: '100%',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}
      />
      <p
        style={{
          margin: '6px 0 0',
          fontFamily: '"Baikal Book"',
          fontSize: '12px',
          color: '#6b7280',
        }}
      >
        {hasPoint
          ? `Pin at ${lat.toFixed(6)}, ${lng.toFixed(6)} — click the map or drag the pin to move it.${
              Number.parseFloat(radiusMeters) > 0 ? ' The shaded ring is the check-in radius.' : ''
            }`
          : 'Click the map to drop a pin, or type coordinates above.'}
      </p>
    </div>
  );
};

export default LocationPickerMap;
